import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { MessageSquare, Send, User, Bot, CheckCircle, UserPlus, Mail, Phone, Clock } from 'lucide-react';

// Liten panel som listar publika chatt-sessioner (anonyma besökare) — särskilt de
// som klickat på "Kontakta en riktig människa" — och låter superadmin svara direkt.
const SuperAdminPublicChats = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollListRef = useRef(null);
  const pollMessagesRef = useRef(null);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.saPublicChatList();
      setSessions(res.data || []);
    } catch {
      // tyst — polling
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (id) => {
    try {
      const res = await api.saPublicChatMessages(id);
      setMessages(res.data.messages || []);
      setSelected((prev) => (prev?.id === id ? { ...prev, ...res.data.session } : prev));
    } catch (e) {
      toast.error('Kunde inte hämta meddelanden');
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    pollListRef.current = setInterval(fetchSessions, 6000);
    return () => clearInterval(pollListRef.current);
  }, [fetchSessions]);

  useEffect(() => {
    if (selected?.id) {
      fetchMessages(selected.id);
      pollMessagesRef.current = setInterval(() => fetchMessages(selected.id), 4000);
      return () => clearInterval(pollMessagesRef.current);
    }
  }, [selected?.id, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !selected) return;
    setSending(true);
    try {
      await api.saPublicChatReply({ session_id: selected.id, message: input });
      setInput('');
      fetchMessages(selected.id);
      fetchSessions();
    } catch {
      toast.error('Kunde inte skicka');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    if (!selected) return;
    try {
      await api.saPublicChatResolve(selected.id);
      toast.success('Markerad som löst');
      setSelected(null);
      setMessages([]);
      fetchSessions();
    } catch {
      toast.error('Kunde inte markera som löst');
    }
  };

  const humanFirst = [...sessions].sort((a, b) => Number(!!b.human_requested) - Number(!!a.human_requested));
  const open = humanFirst.filter((s) => s.status !== 'resolved');
  const resolved = humanFirst.filter((s) => s.status === 'resolved');

  return (
    <div className="flex gap-0 h-[calc(100vh-8rem)] -m-6" data-testid="sa-public-chats-page">
      {/* List */}
      <div className="w-80 flex-shrink-0 bg-card border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <h3 className="font-bold text-sm text-foreground">Publika besökare</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {open.length} öppna · {open.filter((s) => s.human_requested).length} väntar på människa
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-6 text-center text-muted-foreground text-xs">Laddar…</div>
          )}
          {!loading && open.length === 0 && (
            <div className="p-6 text-center text-muted-foreground">
              <MessageSquare size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-xs">Inga publika konversationer</p>
            </div>
          )}
          {open.map((s) => {
            const c = s.contact_info || {};
            const name = c.name || 'Anonym besökare';
            return (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                data-testid={`sa-public-${s.id}`}
                className={`w-full text-left p-3 border-b border-border transition-colors hover:bg-muted/50 ${
                  selected?.id === s.id ? 'bg-muted/70 border-l-2 border-l-[#E8613A]' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-foreground truncate">{name}</p>
                  {s.unread_count > 0 && (
                    <span className="bg-[#E8613A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                      {s.unread_count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {s.last_message?.message?.substring(0, 60) || 'Tomt'}
                </p>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                  {s.human_requested && (
                    <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5">
                      <UserPlus size={10} /> Vill prata med människa
                    </span>
                  )}
                  <span className="flex items-center gap-0.5">
                    <Clock size={10} /> {s.last_message_at?.slice(11, 16) || ''}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {resolved.length > 0 && (
          <details className="border-t border-border max-h-48 overflow-y-auto">
            <summary className="p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/30">
              Lösta ({resolved.length})
            </summary>
            {resolved.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                className="w-full text-left p-3 border-t border-border opacity-60 hover:opacity-100 hover:bg-muted/30"
              >
                <p className="text-sm font-medium truncate">{s.contact_info?.name || 'Anonym'}</p>
                <p className="text-xs text-muted-foreground truncate">{s.last_message?.message?.substring(0, 40)}</p>
              </button>
            ))}
          </details>
        )}
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare size={40} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm">Välj en publik konversation</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header with contact info */}
            <div className="px-5 py-3 border-b border-border bg-card flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-sm text-foreground">
                  {selected.contact_info?.name || 'Anonym besökare'}
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                  {selected.contact_info?.email && (
                    <a href={`mailto:${selected.contact_info.email}`} className="flex items-center gap-1 hover:text-[#E8613A]">
                      <Mail size={11} /> {selected.contact_info.email}
                    </a>
                  )}
                  {selected.contact_info?.phone && (
                    <span className="flex items-center gap-1"><Phone size={11} /> {selected.contact_info.phone}</span>
                  )}
                  <span className={`flex items-center gap-1 ${selected.status === 'resolved' ? 'text-green-700' : 'text-amber-700'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${selected.status === 'resolved' ? 'bg-green-500' : 'bg-amber-500'}`} />
                    {selected.status === 'resolved' ? 'Löst' : 'Pågående'}
                  </span>
                </div>
              </div>
              {selected.status !== 'resolved' && (
                <Button size="sm" variant="outline" onClick={handleResolve} className="text-xs h-8 flex-shrink-0" data-testid="sa-public-resolve">
                  <CheckCircle size={14} className="mr-1" /> Lös
                </Button>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-muted/30" data-testid="sa-public-messages">
              {messages.map((msg) => {
                const isUser = msg.sender === 'user';
                const isSupport = msg.sender === 'support';
                return (
                  <div key={msg.id} className={`flex gap-2 ${isUser ? '' : 'flex-row-reverse'}`}>
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      isUser ? 'bg-muted text-muted-foreground' :
                      isSupport ? 'bg-green-100 text-green-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {isUser ? <User size={14} /> : isSupport ? (msg.sender_name?.charAt(0) || 'S') : <Bot size={14} />}
                    </div>
                    <div className={`max-w-[70%] ${isUser ? '' : 'text-right'}`}>
                      <p className="text-[10px] text-muted-foreground mb-0.5">
                        {isUser ? 'Besökare' : msg.sender_name || (isSupport ? 'Support' : 'AI')}
                        <span className="ml-2">{msg.created_at ? new Date(msg.created_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </p>
                      <div className={`px-3 py-2 rounded-lg text-sm inline-block text-left whitespace-pre-wrap ${
                        isUser ? 'bg-card border border-border' :
                        isSupport ? 'bg-[#E8613A] text-white' :
                        'bg-purple-50 border border-purple-200 text-foreground'
                      }`}>
                        {msg.message}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {selected.status !== 'resolved' && (
              <div className="p-4 border-t border-border bg-card">
                <form onSubmit={handleSend} className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Svara besökaren..."
                    disabled={sending}
                    className="flex-1 h-10 text-sm"
                    data-testid="sa-public-input"
                  />
                  <Button type="submit" disabled={sending || !input.trim()} className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-10 px-4" data-testid="sa-public-send">
                    <Send size={16} />
                  </Button>
                </form>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Ditt svar dyker upp i besökarens chatt direkt + skickas som mail till {selected.contact_info?.email || '(ingen e-post lämnad)'}.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminPublicChats;
